import { useState, useMemo } from 'react';
import { FaCheck } from 'react-icons/fa';
import InputField from '../../../component/form/FormInput';
import rupayImg from '../../../assets/image/checkout/rupay-img.png';
import visaImg from '../../../assets/image/checkout/visa-img.png';
import lockImg from '../../../assets/image/checkout/lock.svg';
import lineImg from '../../../assets/image/checkout/line-img.svg';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { usePayment } from '../../../hooks/usePayment';
import { setBillingType } from '../../../store/reducers/subscriptionSlice/subscriptionSlice';

const COUPONS = {
	SAVE50: 0.5,
	SAVE60: 0.6,
	SAVE80: 0.8,
	SAVE90: 0.9,
	SAVE100: 1.0,
};

export default function OrderSummary() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { selectedPlan, billingType } = useSelector((state) => state.subscription);

	const { billingValid, paymentType, billingData } = useSelector((state) => state.checkout);
	const { initiatePayment, loading } = usePayment();

	const [coupon, setCoupon] = useState('');
	const [appliedCoupon, setAppliedCoupon] = useState(null);

	const basePrice = useMemo(() => {
		return Number(selectedPlan?.price || 0);
	}, [selectedPlan]);

	const discount = useMemo(() => {
		if (!appliedCoupon) return 0;

		const percentage = COUPONS[appliedCoupon] || 0;

		return basePrice * percentage;
	}, [appliedCoupon, basePrice]);

	const tax = useMemo(() => {
		const taxableAmount = basePrice - discount;

		return taxableAmount * 0.18;
	}, [basePrice, discount]);

	const total = useMemo(() => {
		return basePrice - discount + tax;
	}, [basePrice, discount, tax]);

	if (!selectedPlan) {
		navigate('/subscription-plan');

		return null;
	}

	const handleApplyCoupon = () => {
		const normalized = coupon.trim().toUpperCase();

		if (COUPONS[normalized]) {
			setAppliedCoupon(normalized);
			toast.success('Coupon applied');
		} else {
			toast.error('Invalid Coupon');
		}
	};

	const handleRemoveCoupon = () => {
		setAppliedCoupon(null);
		setCoupon('');
	};

	const handlePayment = async () => {
		if (!billingValid) {
			toast.error('Please fill billing details');
			return;
		}

		if (!selectedPlan?.razorpay_plan_id) {
			toast.error('Invalid plan selected');
			return;
		}

		try {
			await initiatePayment({
				billingDetails: billingData,
				paymentType,
				plan: selectedPlan,
				navigate,
			});
		} catch (error) {
			console.error(error);
			toast.error('Payment initialization failed');
		}
	};

	const shouldShowSwitchToStarter = selectedPlan && selectedPlan?.title !== 'Starter';

	return (
		<div className="card-custom order-summary">
			<div className="d-flex justify-content-between align-items-center">
				<h5 className="card-title">Order Summary</h5>
				{shouldShowSwitchToStarter && (
					<span
						onClick={() => navigate('/subscription-plan')}
						className="switch-plan"
					>
						Switch to Starter Plan
					</span>
				)}
			</div>

			<div className="mt-3">
				<div className="d-flex justify-content-between align-items-center">
					<strong>{selectedPlan?.title}</strong>
					<span className="price">₹{basePrice.toFixed(2)}</span>
				</div>

				<div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
					<span className="belling-text fw-semibold">Billing Period</span>

					<select
						className="form-select billing-select"
						value={billingType}
						onChange={(e) => dispatch(setBillingType(e.target.value))}
					>
						<option value="monthly">Monthly</option>
						<option value="yearly">Yearly</option>
					</select>
				</div>
			</div>

			<div className="mt-3">
				<p className="coupon-label">Have a coupon code?</p>

				<div className="d-flex gap-2">
					<div className="flex-grow-1 position-relative">
						<InputField
							value={coupon}
							onChange={setCoupon}
							placeholder="Enter coupon"
							disabled={!!appliedCoupon}
							className="form-control-coupon"
						/>

						{appliedCoupon && <FaCheck className="coupon-check" />}
					</div>

					<button
						type="button"
						className="apply-btn"
						onClick={handleApplyCoupon}
						disabled={!!appliedCoupon}
					>
						Apply
					</button>
				</div>

				{appliedCoupon && (
					<div className="coupon-success-text d-flex justify-content-between">
						<span>Coupon applied successfully</span>
						<span
							className="remove"
							onClick={handleRemoveCoupon}
						>
							Remove
						</span>
					</div>
				)}
			</div>

			<div className="mt-4 d-flex align-items-start gap-3 text-start">
				<div className="d-flex justify-content-center mt-1">
					<img
						src={lineImg}
						alt="timeline"
						style={{ height: '40px' }}
					/>
				</div>

				<div className="d-flex flex-column gap-3 small">
					<div>
						<strong>Today :</strong> August 18, 2025 1 month for <b>₹{basePrice}</b>
					</div>

					<div>
						<strong>Till :</strong> Sept 18, 2025
					</div>
				</div>
			</div>

			<hr />

			<div className="price-row-checkout">
				<span>Sub Total</span>
				<span>₹{basePrice.toFixed(2)}</span>
			</div>

			{discount > 0 && (
				<div className="price-row-checkout text-success">
					<span>Discount ({appliedCoupon})</span>
					<span>-₹{discount.toFixed(2)}</span>
				</div>
			)}

			<div className="price-row-checkout">
				<span>Taxes</span>
				<span>₹{tax.toFixed(2)}</span>
			</div>

			<hr />

			<div className="price-row-checkout total">
				<span>Grand Total</span>
				<span>₹{total.toFixed(2)}</span>
			</div>

			<div className="text-center mt-3">
				<button
					onClick={handlePayment}
					// disabled={!billingValid || loading}
					title={!billingValid ? 'Fill billing details first' : ''}
					className={`btn btn-primary px-4 ${!billingValid || loading ? 'disabled-btn' : ''}`}
				>
					{loading ? 'Processing...' : 'Proceed To Payment'}
				</button>
			</div>

			<div className="secure mt-3">
				<div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
					<div className="d-flex align-items-center gap-2">
						<img
							className="payment-icons-lock"
							src={lockImg}
							alt="lock"
						/>
						<span className="small">Secure Payment</span>
					</div>

					<div className="d-flex align-items-center gap-2">
						<img
							src={visaImg}
							alt="visa"
							className="payment-icon"
						/>
						<img
							src={rupayImg}
							alt="rupay"
							className="payment-icon"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
